import { tva } from '../../tailwind/merge'

/**
 * Widget status, decoupled from the domain enum: `aos-ui-universal` imports no
 * `aos-*` package, so callers map `DashboardItemMode` onto this union.
 */
export type DashboardTileStatus = 'normal' | 'no-data' | 'warning' | 'alert'

/**
 * Web paints warning / alert cards with a 2px border, mobile with a hairline
 * (Figma node 4080:4069). Both looks are preserved rather than harmonised here.
 */
export type DashboardTileBorder = 'hairline' | 'thick'

export interface DashboardTileVariantOptions {
    status?: DashboardTileStatus
    border?: DashboardTileBorder
    /** Web grid cells are hoverable; mobile tiles are not. */
    interactive?: boolean
    /** Warning-reasons state: the card takes the status colour as its fill. */
    filled?: boolean
}

const tile = tva({
    base: 'relative h-full w-full flex-col overflow-hidden rounded-lg border border-solid bg-background-surface',
    variants: {
        status: {
            'normal': 'border-border-subtle',
            'no-data': 'border-border-subtle',
            'warning': 'border-status-warning',
            'alert': 'border-status-critical',
        },
        border: {
            hairline: '',
            thick: '',
        },
        interactive: {
            true: '',
            false: '',
        },
        filled: {
            true: '',
            false: '',
        },
    },
    compoundVariants: [
        { status: 'warning', border: 'thick', class: 'border-2' },
        { status: 'alert', border: 'thick', class: 'border-2' },
        {
            status: 'normal',
            interactive: true,
            class: 'hover:border-border-strong hover:bg-background-surface-hover',
        },
        {
            status: 'no-data',
            interactive: true,
            class: 'hover:border-border-strong hover:bg-background-surface-hover',
        },
        {
            status: 'warning',
            interactive: true,
            class: 'hover:border-status-warning-hover hover:bg-background-surface-hover',
        },
        {
            status: 'alert',
            interactive: true,
            class: 'hover:border-status-critical-hover hover:bg-background-surface-hover',
        },
        // Normal fills with the exact predecessor colour (dashboardItemModeColor[Normal] ===
        // Color.StatusSuccess). No-data deliberately has no fill compound: it's unreachable
        // (getStatusForItemTypeAndHealth forces reasonCodes: [] for no-data, and filled is
        // gated on reasonCodes.length > 0), and its old colour, Color.WidgetSecondary, isn't a
        // status token anyway.
        { status: 'normal', filled: true, class: 'bg-status-success' },
        { status: 'warning', filled: true, class: 'bg-status-warning' },
        { status: 'alert', filled: true, class: 'bg-status-critical' },
    ],
    defaultVariants: {
        status: 'normal',
        border: 'hairline',
        interactive: false,
        filled: false,
    },
})

export const dashboardTileVariants = (options: DashboardTileVariantOptions = {}): string =>
    tile(options)
